import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useQueryClient } from 'react-query';
import { CrudDataGrid, CrudField } from '../../CrudDataGrid';
import { unitCostOverrideApi, UnitCostOverrideReadDTO, UnitCostOverrideCreateDTO } from '../../../services/apiClient';
import { useDiagnosisEnergyVectorsByDiagnosisId } from '../../../hooks';
import { useVectorTypeOptions } from '../../../hooks/useVectorTypeOptions';
import { getEnergyVectorOptionLabel } from '../../common/EnergyVectorDropdown';
import { DECIMAL_PLACES } from '../../../constants/decimalPlacesPolicy';
import { ActionButton } from '../../common';
import { GridRowModel } from '@mui/x-data-grid';

interface UnitCostOverrideDialogProps {
  open: boolean;
  onClose: () => void;
  diagnosisId: string;
}

export const UnitCostOverrideDialog: React.FC<UnitCostOverrideDialogProps> = ({
  open,
  onClose,
  diagnosisId,
}) => {
  const queryClient = useQueryClient();
  const crudRef = React.useRef<{ addRow: () => void }>(null);
  const { data: energyVectors = [] } = useDiagnosisEnergyVectorsByDiagnosisId(diagnosisId);
  const { data: vectorTypeOptions = [] } = useVectorTypeOptions();

  const config = React.useMemo(() => {
    const invalidateInterventions = () => {
      queryClient.invalidateQueries(`diagnosis-interventions-${diagnosisId}`);
    };
    const fields: CrudField[] = [
      {
        field: 'energy_vector_id',
        headerName: 'Vettore energetico',
        width: 220,
        type: 'singleSelect',
        valueOptions: energyVectors.map(v => ({
          value: v.id ?? '',
          label: getEnergyVectorOptionLabel(v, vectorTypeOptions),
        })),
        valueFormatter: (value: string) => {
          const v = energyVectors.find(e => e.id === value);
          return v ? getEnergyVectorOptionLabel(v, vectorTypeOptions) : value ?? '';
        },
      },
      {
        field: 'unit_cost',
        headerName: 'Costo unitario',
        width: 160,
        type: 'number',
        decimalPlaces: DECIMAL_PLACES.consumi_costi_costo_unitario,
      },
    ];

    return {
      queryKey: `unit-cost-override-${diagnosisId}`,
      fields,
      listFn: () => unitCostOverrideApi.listByDiagnosis(diagnosisId),
      createFn: async (data: UnitCostOverrideCreateDTO) => {
        const r = await unitCostOverrideApi.create(data);
        invalidateInterventions();
        return r;
      },
      updateFn: async (id: string, data: UnitCostOverrideCreateDTO) => {
        const r = await unitCostOverrideApi.update(id, data);
        invalidateInterventions();
        return r;
      },
      deleteFn: async (id: string) => {
        await unitCostOverrideApi.delete(id);
        invalidateInterventions();
      },
      getId: (row: UnitCostOverrideReadDTO) => row.id ?? '',
      getCreateDto: (row: GridRowModel): UnitCostOverrideCreateDTO => ({
        diagnosis_id: diagnosisId,
        energy_vector_id: String(row.energy_vector_id ?? ''),
        unit_cost: Number(row.unit_cost) || 0,
      }),
      getDefaultValues: () => ({
        diagnosis_id: diagnosisId,
        energy_vector_id: energyVectors[0]?.id ?? '',
        unit_cost: 0,
      }),
    };
  }, [diagnosisId, energyVectors, vectorTypeOptions, queryClient]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Override costi unitari</span>
          <ActionButton icon={<CloseIcon />} tooltip="Chiudi" onClick={onClose} variant="outlined" />
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <CrudDataGrid
          ref={crudRef}
          config={config}
          height="400px"
        />
      </DialogContent>
      <DialogActions sx={{ flexShrink: 0 }}>
        <Stack direction="row" spacing={2} justifyContent="center" sx={{ width: '100%', py: 1 }}>
          <ActionButton
            icon={<AddIcon />}
            tooltip="Aggiungi override"
            onClick={() => crudRef.current?.addRow()}
            variant="filled"
          />
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
